import { FC, useMemo } from 'react';
import { useAppSelector } from '../../../../lib/store/hooks';
import { useParams } from 'react-router-dom';
import { Form } from 'antd';
import {
  formatArrangedData,
  getArrangesInitialValues,
  getParsedSelectedItems,
  getSelectedItemsLabels,
} from '../../../../lib/utils/arranges-helpers';
import { useStringArrange } from '../../../../features/table/helpers/useStringArrange';
import { TableHeaderCell } from '../../../../components/Table/TableHeaderCell/TableHeaderCell';
import { ArrangeListPopup } from '../../../../features/table/components/popups/ArrangeListPopup';
import { useLazyGetProductsForFilteringQuery } from '../../../../lib/store/api/products';
import { useProductsArrangeRequest } from '../../hooks/useProductsArrangeRequest';
import { useLazyGetProductCategoriesForFilteringQuery } from '../../../../lib/store/api/productCategories';
import { PRODUCTS_COLUMNS } from '../../helpers/consts';
import { useLazyGetDemandAreasForFilteringQuery } from '../../../../lib/store/api/demandArea';

type Props = {
  arrangedId: string;
  title: string;
  searchFieldName?: string;
  filterFieldName?: string;
  //eslint-disable-next-line
  headerCellProps?: any;
  isParsedByValue?: boolean;
  disableFiltering?: boolean;
};

export const ProductSearchDataHeaderCell: FC<Props> = ({
  arrangedId,
  title,
  searchFieldName,
  filterFieldName,
  headerCellProps: additionalHeaderCellProps,
  isParsedByValue,
  disableFiltering,
}) => {
  const sortingAndFiltersData = useAppSelector(({ products }) => products);

  const {
    arrangeId,
    sorting,
    filtering: { filter, filtersWithLabels },
  } = sortingAndFiltersData;

  const [findProducts] = useLazyGetProductsForFilteringQuery();
  const [findProductCategories] = useLazyGetProductCategoriesForFilteringQuery();
  const [findDemandAreas] = useLazyGetDemandAreasForFilteringQuery();

  const { id: tenantId, modelId } = useParams();

  const isActiveArrange = arrangeId === arrangedId;

  const [form] = Form.useForm();
  const getSelectedItems = () => getSelectedItemsLabels(form);

  const { filtering } = useStringArrange({
    form,
    request: (value: string) => {
      if (arrangedId === PRODUCTS_COLUMNS.CATEGORY) {
        return findProductCategories({
          search: value,
          searchFieldName: 'searchByName',
          tenantId: String(tenantId),
          projectId: String(modelId),
        }).unwrap();
      }

      if (arrangedId === PRODUCTS_COLUMNS.DEMAND_AREA) {
        return findDemandAreas({
          search: value,
          searchFieldName: 'searchByName',
          tenantId: String(tenantId),
          projectId: String(modelId),
        }).unwrap();
      }

      return findProducts({
        search: value,
        searchFieldName: String(searchFieldName),
        tenantId: String(tenantId),
        projectId: String(modelId),
      }).unwrap();
    },
    formatResponse: (data: { content: any[] }) => {
      if (arrangedId === PRODUCTS_COLUMNS.CATEGORY || arrangedId === PRODUCTS_COLUMNS.DEMAND_AREA) {
        return data?.content.map((item) => ({
          label: item.name,
          value: JSON.stringify({
            value: item.id,
            label: item.name,
          }),
        }));
      }

      return formatArrangedData(data, arrangedId);
    },
  });

  const { applyArranges, removeFilter, removeFilterValue } = useProductsArrangeRequest({
    arrangedId,
    getFilterLabel: () => getSelectedItems().join(', '),
    getFilters: () => ({ [String(filterFieldName)]: getSelectedItems() }),
    form,
    filterFieldName,
    isParsedByValue,
  });

  const filterLabel = useMemo(() => {
    if (isParsedByValue && Array.isArray(filtersWithLabels)) {
      return filtersWithLabels.map((item) => item.label).join(', ');
    }

    return Array.isArray(filter) && filter.join(', ');
  }, [filter, filtersWithLabels, isParsedByValue]);

  const headerCellProps = {
    arrangedId,
    title,
    removeFilter,
    filterLabel: filterLabel,
    sortOrder: sorting.sortOrder,
    isActiveArrange,
    form,
    applyArranges,
    getParsedItems: () => getParsedSelectedItems(form),
    removeFilterValue,
    ...additionalHeaderCellProps,
  };

  return (
    <TableHeaderCell {...headerCellProps}>
      <ArrangeListPopup
        disableFiltering={disableFiltering}
        initialValues={getArrangesInitialValues(form)}
        form={form}
        filtering={filtering}
        onFinish={applyArranges}
        isActiveArrange={isActiveArrange}
      />
    </TableHeaderCell>
  );
};
